import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { useMediaDevicesContext } from '../../providers/media-devices-context';

// We need 2 seconds of silence within the 5 second test period to be considered
// successful.
const SILENCE_DURATION_MS = 2000;
const TEST_DURATION_MS = 5000;

enum BackgroundNoiseTesterState {
  Testing,
  Success,
  Failure,
}

type BackgroundNoiseTesterProps = {
  noiseThreshold: number;
  onBackgroundNoiseReady?: () => void;
};

export const BackgroundNoiseTester = ({
  noiseThreshold,
  onBackgroundNoiseReady,
}: BackgroundNoiseTesterProps) => {
  const { analyser, selectedMicrophone } = useMediaDevicesContext();
  const [testerState, setTesterState] = useState<BackgroundNoiseTesterState>(
    BackgroundNoiseTesterState.Testing,
  );

  useEffect(() => {
    if (
      !selectedMicrophone ||
      !analyser ||
      testerState !== BackgroundNoiseTesterState.Testing
    ) {
      return;
    }

    // Check if the background noise is below a certain threshold. Only enable
    // the Next button when the background noise is below the threshold.
    const sampleBuffer = new Float32Array(analyser.frequencyBinCount);
    const testStartTime = Date.now();
    let lastAboveThresholdTime = testStartTime;

    let intervalId: NodeJS.Timeout | undefined = setInterval(() => {
      const now = Date.now();
      let isNoiseAboveThreshold = false;
      analyser.getFloatFrequencyData(sampleBuffer);

      for (let i = 0; i < sampleBuffer.length; i++) {
        if (sampleBuffer[i] > noiseThreshold) {
          isNoiseAboveThreshold = true;
          break;
        }
      }

      // If the test duration has passed, we consider the test failed.
      if (now - testStartTime > TEST_DURATION_MS) {
        clearInterval(intervalId);
        intervalId = undefined;
        setTesterState(BackgroundNoiseTesterState.Failure);
        return;
      }

      // If the background noise is below the threshold for the required
      // duration, we consider the test successful.
      if (now - lastAboveThresholdTime > SILENCE_DURATION_MS) {
        clearInterval(intervalId);
        intervalId = undefined;
        setTesterState(BackgroundNoiseTesterState.Success);
        onBackgroundNoiseReady?.();
        return;
      }

      if (isNoiseAboveThreshold) {
        lastAboveThresholdTime = now;
      }
    }, 100);

    return () => intervalId && clearInterval(intervalId);
  }, [
    analyser,
    selectedMicrophone,
    noiseThreshold,
    testerState,
    onBackgroundNoiseReady,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {testerState === BackgroundNoiseTesterState.Testing && (
        <CircularProgress
          sx={{
            width: 64,
            height: 64,
          }}
        />
      )}
      {testerState === BackgroundNoiseTesterState.Success && (
        <CheckCircleOutlineIcon
          sx={{
            width: 64,
            height: 64,
            color: 'success.main',
          }}
        />
      )}
      {testerState === BackgroundNoiseTesterState.Failure && (
        <ErrorOutlineIcon
          sx={{
            width: 64,
            height: 64,
            color: 'error.main',
          }}
        />
      )}

      <Typography variant='body1'>
        {testerState === BackgroundNoiseTesterState.Testing
          ? 'Checking for background noise...'
          : testerState === BackgroundNoiseTesterState.Success
            ? 'Perfect! You can proceed to the next step.'
            : 'There seems to be background noise. Please try again in a quiet environment.'}
      </Typography>

      {testerState === BackgroundNoiseTesterState.Failure && (
        <Button
          variant='contained'
          onClick={() => setTesterState(BackgroundNoiseTesterState.Testing)}
        >
          Try again
        </Button>
      )}
    </Box>
  );
};
